import React, { useMemo, useState } from 'react';
import { useTheme } from '../../ThemeContext';

/**
 * Reusable typed confirmation popup. Replicates the design and theming approach of BillingRestrictionPopup.
 *
 * Props:
 * - visible: boolean
 * - title: string
 * - message: string | ReactNode
 * - confirmWord: string (default 'CONFIRM') - case-sensitive
 * - onConfirm: Function
 * - onClose: Function
 * - confirmButtonLabel: string (default 'Confirm') - rendered in all caps visually
 * - secondaryButtonLabel: string (default 'Close')
 */
const TypedConfirmPopup = ({
  visible,
  title = 'Confirm Action',
  message,
  confirmWord = 'CONFIRM',
  onConfirm,
  onClose,
  confirmButtonLabel = 'Confirm',
  secondaryButtonLabel = 'Close',
}) => {
  const { themeMode, currentTheme } = useTheme();
  const [inputValue, setInputValue] = useState('');

  const isMatch = useMemo(() => inputValue === confirmWord, [inputValue, confirmWord]);

  if (!visible) return null;

  const overlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    backdropFilter: 'blur(4px)',
    WebkitBackdropFilter: 'blur(4px)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 10000,
    animation: 'fadeIn 0.3s ease-out'
  };

  const popupStyle = {
    backgroundColor: themeMode === 'dark' ? '#04002b' : '#ffffff',
    color: currentTheme.homePage.textColor,
    borderRadius: '16px',
    padding: 0,
    maxWidth: '560px',
    width: '90%',
    maxHeight: '90vh',
    overflow: 'hidden',
    boxShadow: themeMode === 'dark'
      ? '0 20px 40px rgba(0, 0, 0, 0.6), 0 0 0 1px rgba(255, 255, 255, 0.1)'
      : '0 20px 40px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0, 0, 0, 0.05)',
    border: themeMode === 'dark' ? '1px solid #302d4c' : '1px solid #e9e8ea',
    animation: 'slideIn 0.3s ease-out',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', sans-serif"
  };

  const headerStyle = {
    padding: '24px 24px 0 24px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    borderBottom: `1px solid ${themeMode === 'dark' ? '#302d4c' : '#e9e8ea'}`,
    paddingBottom: '16px',
    marginBottom: '24px'
  };

  const titleStyle = {
    fontSize: '20px',
    fontWeight: 600,
    color: '#e63e2b',
    margin: 0,
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  };

  const closeButtonStyle = {
    background: 'none',
    border: 'none',
    fontSize: '24px',
    color: currentTheme.homePage.textColor,
    cursor: 'pointer',
    padding: '4px',
    borderRadius: '4px',
    opacity: 0.7,
    transition: 'opacity 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '32px',
    height: '32px'
  };

  const contentStyle = {
    padding: '0 24px',
    marginBottom: '24px'
  };

  const messageStyle = {
    fontSize: '16px',
    lineHeight: 1.6,
    color: currentTheme.homePage.textColor,
    margin: 0,
    textAlign: 'left'
  };

  const inputLabelStyle = {
    marginTop: '16px',
    marginBottom: '8px',
    fontSize: '14px',
    opacity: 0.85
  };

  const inputStyle = {
    width: '95this %',
    height: '44px',
    borderRadius: '8px',
    border: `1px solid ${themeMode === 'dark' ? '#302d4c' : '#e9e8ea'}`,
    backgroundColor: themeMode === 'dark' ? '#0b073a' : '#ffffff',
    color: currentTheme.homePage.textColor,
    padding: '0 12px',
    outline: 'none'
  };

  const footerStyle = {
    padding: '0 24px 24px 24px',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px'
  };

  const buttonBaseStyle = {
    padding: '12px 24px',
    borderRadius: '8px',
    border: 'none',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    minWidth: '100px',
    height: '44px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  };

  const secondaryButtonStyle = {
    ...buttonBaseStyle,
    backgroundColor: themeMode === 'dark' ? '#302d4c' : '#e9e8ea',
    color: currentTheme.homePage.textColor
  };

  const dangerButtonStyle = {
    ...buttonBaseStyle,
    backgroundColor: isMatch ? '#e63e2b' : (themeMode === 'dark' ? '#4b476a' : '#f0f0f0'),
    color: isMatch ? '#ffffff' : (themeMode === 'dark' ? '#a6a3bf' : '#8a8a8a'),
    cursor: isMatch ? 'pointer' : 'not-allowed'
  };

  const handleConfirm = () => {
    if (!isMatch) return;
    onConfirm?.();
  };

  return (
    <>
      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes slideIn {
            from {
              opacity: 0;
              transform: translateY(-20px) scale(0.95);
            }
            to {
              opacity: 1;
              transform: translateY(0) scale(1);
            }
          }
        `}
      </style>
      <div style={overlayStyle} onClick={onClose}>
        <div style={popupStyle} onClick={(e) => e.stopPropagation()}>
          <div style={headerStyle}>
            <h2 style={titleStyle}>{title}</h2>
            <button
              style={closeButtonStyle}
              onClick={onClose}
              onMouseEnter={(e) => e.target.style.opacity = '1'}
              onMouseLeave={(e) => e.target.style.opacity = '0.7'}
            >
              ×
            </button>
          </div>

          <div style={contentStyle}>
            <p style={messageStyle}>{message}</p>
            <div style={inputLabelStyle}>
              Type "{confirmWord}" to proceed. This action is irreversible.
            </div>
            <input
              style={inputStyle}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder={confirmWord}
              autoFocus
            />
          </div>

          <div style={footerStyle}>
            <button
              style={secondaryButtonStyle}
              onClick={onClose}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = themeMode === 'dark' ? '#404040' : '#d0d0d0';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = themeMode === 'dark' ? '#302d4c' : '#e9e8ea';
              }}
            >
              {secondaryButtonLabel}
            </button>
            <button
              style={dangerButtonStyle}
              disabled={!isMatch}
              onClick={handleConfirm}
              onMouseEnter={(e) => {
                if (isMatch) e.target.style.backgroundColor = '#d32f2f';
              }}
              onMouseLeave={(e) => {
                if (isMatch) e.target.style.backgroundColor = '#e63e2b';
              }}
            >
              {String(confirmButtonLabel).toUpperCase()}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default TypedConfirmPopup;

